// Copyright (c) 2020-23, iPivot, Corp.
'use strict'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLock, faUser } from '@fortawesome/free-solid-svg-icons'
import axios from 'axios'
import PropTypes from 'prop-types'
import { isObject } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { Button, Card, Col, Container, Form, InputGroup, ProgressBar, Row, Spinner } from 'react-bootstrap'
import { withTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import styled, { withTheme } from 'styled-components'

import { getImage } from '@ipivot/themes-browser'

import EulaModal from 'js/components/modals/EulaModal'
import ChangePasswordModal from 'js/components/modals/ChangePasswordModal'
import WaitModal from 'js/components/modals/WaitModal'

const ENTER_KEY = 13

const Login = ({ t, theme }) => {
    const [username, setUsername] = useState('')
    const [errorMessage, setErrorMessage] = useState(null)
    const [isAuthenticating, setIsAuthenticating] = useState(false)
    const [eulaAccepted, setEulaAccepted] = useState(false)
    const [eulaCovered, setEulaCovered] = useState(false)
    const [showEulaModal, setShowEulaModal] = useState(false)
    const [showUsername, setShowUsername] = useState(true)
    const [showPassword, setShowPassword] = useState(true)
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false)
    const [certPresent, setCertPresent] = useState(false)
    const [certBypass, setCertBypass] = useState(false)
    const [motd, setMotd] = useState('')
    const [loginNotice, setLoginNotice] = useState('')
    const [showWaitModal, setShowWaitModal] = useState(false)
    const [ssoEnabled, setSsoEnabled] = useState(false)
    const [ssoErrorMessage, setSsoErrorMessage] = useState(null)
    const [unitName, setUnitName] = useState(null)
    const [systemLocked, setSystemLocked] = useState(false)
    const [systemLockedMessage, setSystemLockedMessage] = useState(null)

    const usernameInputRef = useRef()
    const passwordInputRef = useRef()

    const fetchEula = async () => {
        try {
            const data = (await axios.get('/eula')).data
            setShowEulaModal(data.eulaRequired)
        } catch (err) {
            setShowEulaModal(true)
        }
    }

    const fetchLoginUnitData = async () => {
        try {
            const data = (await axios.get('/systemManagement/loginUnitData')).data
            setUnitName(data.unitName)
            setLoginNotice(data.loginNotice)
            setMotd(data.motd)
            setShowUsername(data.showUsername)
            setShowPassword(data.showPassword)
            setCertPresent(data.certPresent)
            setSsoEnabled(data.ssoEnabled)
        } catch (err) {
            //Do nothing
        }
    }

    const fetchSystemLock = async () => {
        try {
            const data = (await axios.get('/systemManagement/locked')).data
            setSystemLocked(data.locked)
            setSystemLockedMessage(data.message)
        } catch (err) {
            //Do nothing
        }
    }

    useEffect(() => {
        fetchEula()
    }, [])
    useEffect(() => {
        fetchLoginUnitData()
    }, [])
    useEffect(() => {
        fetchSystemLock()
    }, [])

    useEffect(() => {
        if (usernameInputRef.current) usernameInputRef.current.focus()

        let url = new URL(location.href)
        if (url.searchParams.get('ssoError') === 'true') {
            setSsoErrorMessage(t('login.unexpected_sso_login_error_label'))
            url.searchParams.delete('ssoError')
            history.pushState({}, '', url)
        }
    }, [t])

    const closeEulaModal = () => {
        setShowEulaModal(false)
    }

    const closeChangePasswordModal = newPassword => {
        if (_.isUndefined(newPassword)) {
            return
        }

        passwordInputRef.current.value = newPassword
        setErrorMessage(null)
        setShowChangePasswordModal(false)
        resetLogin() // Logout and then log back in to set normal capabilties
    }

    const acceptEulaModal = () => {
        setShowEulaModal(false)
        setEulaAccepted(true)
    }

    const coveredEulaModal = () => {
        setShowEulaModal(false)
        setEulaAccepted(true)
        setEulaCovered(true)
    }

    const submitOnEnter = e => {
        if (e.charCode === ENTER_KEY && username !== '') {
            login()
        }
    }

    const bypassCert = e => {
        e.preventDefault()
        setCertBypass(true)
    }

    const returnToCert = e => {
        e.preventDefault()
        setCertBypass(false)
    }

    const login = async () => {
        try {
            setErrorMessage(null)
            setIsAuthenticating(true)

            const certPresent = showPassword === false || showUsername === false

            let postData = { eulaAccepted, eulaCovered, certBypass }

            if (certPresent) {
                postData.username = showUsername || certBypass ? username : undefined
                postData.password = showPassword || certBypass ? passwordInputRef.current.value : undefined
            } else {
                postData.username = username
                postData.password = passwordInputRef.current.value
            }

            const data = (await axios.post('/login', postData)).data

            if (isObject(data) && data.passwordExpired) {
                setShowChangePasswordModal(true)
                setErrorMessage(t('login.password_expired_label'))
                setIsAuthenticating(false)
            } else {
                setShowChangePasswordModal(false)
                setErrorMessage('')
                setIsAuthenticating(false)

                let url = new URL(location.href)
                if (url.searchParams.get('redirectTo') === 'true') {
                    window.location.href = url.searchParams.get('redirectTo')
                } else {
                    window.location.href = '/enc'
                }
            }
        } catch (err) {
            let errorMessage

            switch (err.response.status) {
                case 0:
                    //Show wait modal which will continually ping the web server until it comes back up
                    setShowWaitModal(true)
                    setIsAuthenticating(false)
                    return
                case 401:
                    switch (err.response.data) {
                        case 'Account expired':
                            errorMessage = t('login.account_expired_label')
                            break
                        case 'Account locked':
                            errorMessage = t('login.account_locked_label')
                            break
                        case 'Password expired':
                        // This means the password policy was set to 'reject' vs 'forceChange'
                        // fall through
                        default:
                            // Generic error message to avoid giving bad guy too much detail
                            errorMessage = t('login.log_in_failed_label')
                            break
                    }

                    break
                default:
                    errorMessage = t('login.unexpected_login_error_label')
                    break
            }

            setErrorMessage(errorMessage)
            setIsAuthenticating(false)
        }
    }

    const ssoRequest = async () => {
        try {
            const data = (await axios.get('/saml/request')).data
            window.location = data.response
        } catch (err) {
            setSsoErrorMessage(t('login.unexpected_login_error_label'))
        }
    }

    const resetLogin = async () => {
        try {
            await axios.post('/logout')
            await login()
        } catch (err) {
            //Do nothing
        }
    }

    const error = errorMessage ? <span className="text-danger">{errorMessage}</span> : null

    let systemLockedIndicator = null
    if (systemLocked) {
        systemLockedIndicator = <StyledProgressBar striped animated variant="info" now={100} label={systemLockedMessage} className="fixed-top" />
    }

    const messageOfTheDay =
        motd === undefined || motd === '' ? null : (
            <MotdRow>
                <Col sm={12}>
                    <Motd className="text-center">
                        <ReactMarkdown escapeHtml={true} source={motd} />
                    </Motd>
                </Col>
            </MotdRow>
        )

    const loginNoticePanel =
        loginNotice !== '' ? (
            <div>
                <ReactMarkdown escapeHtml={true} source={loginNotice} />
            </div>
        ) : null

    let credentialContainer = null
    let bypassLink = null

    if (certPresent === true && certBypass === false) {
        bypassLink = (
            <BypassCert onClick={bypassCert} className="text-center">
                {t('login.login_as_different_user_label')}
            </BypassCert>
        )

        const credentialUsername =
            showUsername === true ? (
                <Form.Group className="mb-3">
                    <InputGroup>
                        <InputGroup.Text>
                            <FontAwesomeIcon icon={faUser} />
                        </InputGroup.Text>
                        <Form.Control
                            id="username"
                            type="text"
                            autoComplete="off"
                            autoFocus={true}
                            placeholder={t('login.username_placeholder')}
                            value={username}
                            disabled={isAuthenticating}
                            onChange={e => setUsername(e.target.value)}
                            ref={usernameInputRef}
                        />
                    </InputGroup>
                </Form.Group>
            ) : null

        const credentialPassword =
            showPassword === true ? (
                <Form.Group className="mb-3">
                    <InputGroup>
                        <InputGroup.Text>
                            <FontAwesomeIcon icon={faLock} />
                        </InputGroup.Text>
                        <Form.Control
                            id="password"
                            type="password"
                            autoComplete="off"
                            placeholder={t('login.password_placeholder')}
                            disabled={isAuthenticating}
                            ref={passwordInputRef}
                        />
                    </InputGroup>
                </Form.Group>
            ) : null

        credentialContainer = (
            <div>
                <CertificateDetected className="text-center">
                    <FontAwesomeIcon icon={faLock} /> {t('login.certificate_detected_label')}
                </CertificateDetected>
                {credentialUsername}
                {credentialPassword}
            </div>
        )
    } else {
        if (certBypass === true) {
            bypassLink = (
                <BypassCert onClick={returnToCert} className="text-center">
                    {t('login.login_using_certificate_label')}
                </BypassCert>
            )
        }

        credentialContainer = (
            <>
                <Form.Group className="mb-3">
                    <InputGroup>
                        <InputGroup.Text>
                            <FontAwesomeIcon icon={faUser} />
                        </InputGroup.Text>
                        <Form.Control
                            id="username"
                            type="text"
                            autoComplete="off"
                            autoFocus={true}
                            placeholder={t('login.username_placeholder')}
                            value={username}
                            disabled={isAuthenticating || (certPresent && !certBypass)}
                            onChange={e => setUsername(e.target.value)}
                            ref={usernameInputRef}
                        />
                    </InputGroup>
                </Form.Group>
                <Form.Group className="mb-3">
                    <InputGroup>
                        <InputGroup.Text>
                            <FontAwesomeIcon icon={faLock} />
                        </InputGroup.Text>
                        <Form.Control
                            id="password"
                            type="password"
                            autoComplete="off"
                            placeholder={t('login.password_placeholder')}
                            disabled={isAuthenticating || (certPresent && !certBypass)}
                            ref={passwordInputRef}
                        />
                    </InputGroup>
                </Form.Group>
            </>
        )
    }

    let ssoContainer = null
    let ssoErrorContainer = null
    if (ssoEnabled) {
        ssoContainer = (
            <>
                <hr />
                <div className="text-center">
                    <Button title={t('login.single_sign_on_title')} onClick={ssoRequest}>
                        {t('login.single_sign_on_title')}
                    </Button>
                </div>
            </>
        )

        if (ssoErrorMessage) {
            ssoErrorContainer = <div className="text-center text-danger">{ssoErrorMessage}</div>
        }
    }

    const isValid = certPresent || username !== ''

    return (
        <Container fluid>
            <Row>
                <Col sm={12}>{messageOfTheDay}</Col>
            </Row>
            <Row>
                <Col sm={12}>{systemLockedIndicator}</Col>
            </Row>
            <Row>
                <Col sm={12}>
                    <LoginForm onKeyPress={submitOnEnter}>
                        <Brand>
                            {theme.images.mark ? (
                                <>
                                    <BrandMark src={getImage(theme.images.mark)} alt={theme.images.logoImgAlt} />
                                    <BrandName>{theme.images.logoImgAlt}</BrandName>
                                </>
                            ) : theme.images.loginLogo ? (
                                <BrandLogo src={getImage(theme.images.loginLogo)} alt={theme.images.logoImgAlt} />
                            ) : null}
                            {theme.productName ? <ProductName>{theme.productName}</ProductName> : null}
                        </Brand>
                        <Card>
                            <Card.Body>
                                {loginNoticePanel}
                                <div className="text-end">
                                    {credentialContainer}
                                    {error}
                                    {bypassLink}
                                    <Button
                                        className="ms-2"
                                        title={t('login.log_in_title')}
                                        disabled={isAuthenticating || !isValid}
                                        onClick={login}
                                    >
                                        {isAuthenticating ? <Spinner /> : t('login.log_in_title')}
                                    </Button>
                                </div>
                                {ssoContainer}
                                {ssoErrorContainer}
                            </Card.Body>
                        </Card>
                        <div className="text-center">
                            <UnitName>{unitName}</UnitName>
                        </div>
                    </LoginForm>
                </Col>
            </Row>

            {showEulaModal && <EulaModal onClose={closeEulaModal} onAccept={acceptEulaModal} onCovered={coveredEulaModal} />}

            {showChangePasswordModal ? (
                <ChangePasswordModal
                    show={true}
                    isCancelable={false}
                    title={t('login.password_expired_title')}
                    onClose={newPW => closeChangePasswordModal(newPW)}
                />
            ) : null}

            {showWaitModal ? (
                <WaitModal
                    title={t('login.unit_restarting_title')}
                    msgTop={t('login.unit_currently_restarting_label')}
                    msgBottom={t('login.unit_comes_back_online_label')}
                />
            ) : null}
        </Container>
    )
}

Login.propTypes = {
    t: PropTypes.func.isRequired,
    theme: PropTypes.object.isRequired,
}

export default withTranslation()(withTheme(Login))

//Style
const LoginForm = styled(Form)`
    width: 400px;
    margin: auto;
    padding-top: 75px;
    padding-bottom: 75px;
`

/* The brand lockup above the credentials card. Themes that ship a standalone `mark`
 * get it stacked over live type, so the product name and the company name are real text
 * -- they scale, they take the theme's colour, and they are readable to a screen reader.
 * Themes that only have a baked logo image fall back to that.
 */
const Brand = styled.div`
    padding: 3rem 0 2rem 0;
    text-align: center;
`

const BrandMark = styled.img`
    display: block;
    width: 96px;
    height: auto;
    margin: 0 auto 0.75rem auto;
`

/* Baked logo art is wider than the 400px form, so it has to be free to scale down;
 * without the max-width it overflows the form instead of centering in it.
 */
const BrandLogo = styled.img`
    display: block;
    max-width: 100%;
    height: auto;
    margin: 0 auto;
`

const BrandName = styled.div`
    font-size: 22px;
    font-weight: 600;
    letter-spacing: -0.01em;
    line-height: 1.2;
`

const ProductName = styled.div`
    font-size: 15px;
    font-weight: 400;
    opacity: 0.75;
    margin-top: 0.125rem;
`

const MotdRow = styled(Row)`
    background: ${props => props.theme.colors.motd.backgroundColor};
    color: ${props => props.theme.colors.motd.color};
    box-shadow: 0 0 5px ${props => props.theme.colors.motd.boxShadowColor};
    padding: 10px 0px 0px 0px;
`

const Motd = styled.div`
    font-size: 16px;
    font-weight: 500;
`

const BypassCert = styled.a`
    padding-right: 10px;
`

const CertificateDetected = styled.div`
    padding: 0px 10px 10px 0px;
`

const UnitName = styled.p`
    padding-top: 0px;
`

const StyledProgressBar = styled(ProgressBar)`
    height: 30px !important;
    font-size: 14px !important;
    font-weight: bold;
`
